/* ============================================================
   The purchase surface — the web counterpart of app/src/screens/PaywallScreen.

   Two hosts, one panel:
     • the GATE (App.tsx): an account with no plan at all sees this instead of
       the app, full-screen, with the sky scene beside it;
     • the DIALOG (AppShell, Settings): a lapsed account keeps reading its data
       (docs/subscriptions.md §6) and meets the same panel in a sheet when it
       tries to write or taps "Choose a plan".

   Tone follows the iOS screen and CLAUDE.md: no countdown, no "don't lose
   your progress", no crossed-out anchor price. State what Pro is, state what
   it costs, and let the two weeks do the persuading. Prices come from the
   store, never hardcoded.

   Honesty lines that are specific to the web:
     • there is no "Restore purchases" — a web subscription is bound to the
       account, so the only recovery an account can need is "the server has not
       noticed yet", which is the "I already subscribed" button;
     • cancellation is a link in the receipt email and in Settings, not an OS
       sheet, so the disclosure says exactly that;
     • web checkout is not offered in the EU yet (no VAT registration), so the
       paywall says so rather than letting a checkout fail on tax.
   ============================================================ */
import { useCallback, useEffect, useState } from 'react';
import { isPlan, isPlanReason, isPlanStore } from '@habishine/shared';
import { useApi, useAuth, useEntitlement } from '@habishine/client-core';

import { screen, track } from '../analytics';
import { Sky } from '../components/Sky';
import { SunMark } from '../components/SunMark';
import { Button } from '../components/ui';
import { APP_STORE_URL } from '../config';
import { billingAvailable, billingIsSandbox, loadOffers, purchase, type WebOffer } from '../platform/billing';

const TERMS_URL = 'https://habishine.com/terms.html';
const PRIVACY_URL = 'https://habishine.com/privacy.html';

const PERKS = [
  'Everything on your iPhone and in this browser, always in sync',
  'Morning plan and evening review, as reminders where you are',
  'Projects, tags, repeating tasks and your whole habit history',
];

/** The panel itself: plan picker, checkout button, disclosures. */
export function PaywallPanel({ compact = false, onPurchased }: { compact?: boolean; onPurchased?: () => void }) {
  const { user } = useAuth();
  const api = useApi();
  const { plan, planReason, grantOptimistically, adopt } = useEntitlement();

  const [offers, setOffers] = useState<WebOffer[] | null>(billingAvailable ? null : []);

  // The gate is the hard wall (no onPurchased: nothing to return to); the
  // compact panel in a sheet is the upgrade ask — the phone's two sources.
  useEffect(() => {
    track('paywall_view', { source: onPurchased ? 'upgrade' : 'hard' });
    screen('paywall');
  }, [onPurchased]);
  const [selected, setSelected] = useState<string | null>(null);
  const [busy, setBusy] = useState<'buy' | 'check' | null>(null);
  const [note, setNote] = useState<string | null>(null);

  useEffect(() => {
    if (!billingAvailable) return;
    let alive = true;
    void loadOffers().then((list) => {
      if (!alive) return;
      setOffers(list);
      setSelected((s) => s ?? list[0]?.id ?? null);
    });
    return () => {
      alive = false;
    };
  }, []);

  const chosen = offers?.find((o) => o.id === selected) ?? null;
  const trial = chosen?.trial ?? null;

  const onBuy = useCallback(async () => {
    if (!chosen || busy) return;
    setBusy('buy');
    setNote(null);
    const plan = { plan: chosen.id, period: chosen.period ?? 'unknown' };
    track('begin_checkout', plan);
    const outcome = await purchase(chosen, user?.email);
    setBusy(null);
    if (outcome.kind === 'purchased') {
      track(chosen.trial ? 'trial_started' : 'subscription_purchased', plan);
      // Unlock NOW; the server catches up through reconcile and the webhook.
      grantOptimistically('web');
      onPurchased?.();
    } else if (outcome.kind === 'cancelled') {
      track('purchase_cancelled', plan);
    } else if (outcome.kind === 'error') {
      setNote(outcome.message);
    }
  }, [chosen, busy, user?.email, grantOptimistically, onPurchased]);

  /** "I already subscribed" — on an iPhone, or here a moment ago on another
      tab. Asks the server to re-check with RevenueCat and adopts its answer;
      nothing is claimed, the server does the looking (handlers/billing.ts). */
  const onCheck = useCallback(async () => {
    if (busy) return;
    setBusy('check');
    setNote(null);
    try {
      const res = await api.post<{ plan?: unknown; planReason?: unknown; planStore?: unknown }>('/v1/billing/reconcile');
      if (isPlan(res?.plan)) {
        adopt(res.plan, isPlanReason(res?.planReason) ? res.planReason : null, isPlanStore(res?.planStore) ? res.planStore : null);
        if (res.plan === 'none' || res.plan === 'expired') setNote('No active subscription found on this account yet.');
      }
    } catch {
      setNote('Couldn’t check right now — try again in a moment.');
    }
    setBusy(null);
  }, [busy, api, adopt]);

  const transferred = plan === 'expired' && planReason === 'transferred';

  return (
    <div className={compact ? '' : 'max-w-[420px]'}>
      <div className="mb-2 flex items-center gap-2 text-[12px] font-bold uppercase tracking-[1.4px] text-frog">
        Habishine Pro
        {billingIsSandbox ? <span className="rounded-full bg-nudge px-2 py-0.5 text-[10px] tracking-[0.6px] text-replan">Sandbox</span> : null}
      </div>
      {!compact ? (
        <h1 className="font-display text-[34px] font-semibold leading-[1.05] text-ink md:text-[38px]">
          {plan === 'expired' ? 'Pick up where you left off.' : 'Two weeks free. Then one plan, everywhere.'}
        </h1>
      ) : null}
      {transferred ? (
        <p className="my-4 rounded-tile bg-nudge px-3.5 py-3 text-[14px] leading-[1.5] text-ink">
          This account’s subscription moved to another Habishine account on your Apple ID. Subscribe here to keep using this one, or switch accounts.
        </p>
      ) : null}

      <ul className="my-5 space-y-2 text-[14.5px] leading-[1.5] text-ink-2">
        {PERKS.map((p) => (
          <li key={p} className="flex gap-2.5">
            <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-frog" />
            <span>{p}</span>
          </li>
        ))}
      </ul>

      {!billingAvailable ? (
        <p className="my-5 text-[15px] leading-[1.55] text-ink-2">
          Subscribe in the Habishine iOS app, then come back — your plan syncs here within a moment.{' '}
          <a className="font-semibold text-frog hover:underline" href={APP_STORE_URL} target="_blank" rel="noreferrer">Get the app</a>
        </p>
      ) : offers === null ? (
        <div className="my-6 flex items-center gap-3 text-sm text-meta">
          <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-frog border-t-transparent" /> Loading plans…
        </div>
      ) : offers.length === 0 ? (
        <p className="my-5 text-[14.5px] leading-[1.5] text-meta">Plans couldn’t be loaded right now. Check your connection and try again.</p>
      ) : (
        <div className="space-y-2.5" role="radiogroup" aria-label="Plan">
          {offers.map((o) => {
            const on = o.id === selected;
            const name = o.period === 'year' ? 'Every year' : o.period === 'month' ? 'Every month' : 'Subscription';
            return (
              <button
                key={o.id}
                type="button"
                role="radio"
                aria-checked={on}
                onClick={() => setSelected(o.id)}
                className={`flex w-full items-center gap-3 rounded-tile border px-4 py-3 text-left transition-[border-color,box-shadow,background-color] duration-150 ${
                  on ? 'border-frog bg-frog-wash shadow-[0_0_0_3px_var(--frog-wash)]' : 'border-hairline bg-card hover:bg-card-sunk'
                }`}
              >
                <span className={`h-4.5 w-4.5 shrink-0 rounded-full border-2 ${on ? 'border-frog bg-frog' : 'border-hairline'}`} aria-hidden />
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-2 text-[15px] font-semibold text-ink">
                    {name}
                    {o.period === 'year' ? <span className="rounded-full bg-[var(--hue-green-t)] px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-[0.6px] text-[var(--hue-green-k)]">Best value</span> : null}
                  </span>
                  <span className="block text-[13px] text-meta">
                    {o.price}{o.period ? ` / ${o.period}` : ''}{o.perMonth ? ` · ${o.perMonth} a month` : ''}
                  </span>
                </span>
              </button>
            );
          })}
          <Button variant="primary" className="mt-2 w-full" busy={busy === 'buy'} disabled={!chosen || busy !== null} onClick={() => void onBuy()}>
            {trial ? `Start my free ${trial}` : 'Subscribe'}
          </Button>
        </div>
      )}

      {note ? <p className="mt-3 text-[13px] text-replan">{note}</p> : null}

      {billingAvailable && offers && offers.length > 0 ? (
        <p className="mt-4 text-[12.5px] leading-[1.55] text-meta">
          {trial
            ? `Your ${trial} are free. After that the subscription renews automatically at the price above until you cancel. `
            : 'The subscription renews automatically at the price above until you cancel. '}
          Cancel any time from Settings → Subscription, or from the link in your receipt. Prices in USD; web checkout isn’t available in the EU yet.
        </p>
      ) : null}

      <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px]">
        <button type="button" className="font-semibold text-frog hover:underline disabled:opacity-50" disabled={busy !== null} onClick={() => void onCheck()}>
          {busy === 'check' ? 'Checking…' : 'I already subscribed'}
        </button>
        <span className="text-tertiary">·</span>
        <a className="text-tertiary hover:underline" href={TERMS_URL} target="_blank" rel="noreferrer">Terms of Use</a>
        <span className="text-tertiary">·</span>
        <a className="text-tertiary hover:underline" href={PRIVACY_URL} target="_blank" rel="noreferrer">Privacy Policy</a>
      </div>
    </div>
  );
}

/** The full-screen gate: an account with no plan at all. Sign-out stays
    reachable — with a hard wall this is the only screen such an account can
    reach, so an exit lives here or the account is a trap. */
export function PaywallGate() {
  const { signOut } = useAuth();
  return (
    <div className="grain grid h-full lg:grid-cols-2 lg:grid-rows-[minmax(0,1fr)]">
      <Sky variant="scene" progress={0.4} className="hidden h-full lg:block">
        <div className="flex h-full flex-col justify-between p-12">
          <div className="flex items-center gap-3"><SunMark size={34} /><span className="wordmark text-[24px] text-[var(--sky-ink)]">habishine</span></div>
          <h2 className="font-display max-w-[12ch] text-[52px] font-semibold leading-[1.02] text-[var(--sky-ink)]">Your plan syncs here too.</h2>
        </div>
      </Sky>
      <div className="relative z-[1] flex min-h-0 items-start justify-center overflow-y-auto px-5 py-8 lg:items-center lg:px-8">
        <div className="screen-in w-full max-w-[420px]">
          <PaywallPanel />
          <div className="mt-6">
            <Button variant="ghost" onClick={() => void signOut()}>Sign out</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
